// =============================================================================
// ESPOLIDAR WEB ADMIN - MEDIA PAGE
// =============================================================================

import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { useMedia, useUploadMedia, useDeleteMedia } from '../hooks/useMedia'
import {
  Image,
  Video,
  Music,
  Upload,
  Trash2,
  Download,
  File,
  Filter,
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import { mediaApi } from '../services/api'
import type { Media } from '../types'

const formatBytes = (bytes: number) => {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const MediaIcon = ({ type }: { type: string }) => {
  switch (type) {
    case 'video':
      return <Video size={24} className="text-blue-500" />
    case 'image':
      return <Image size={24} className="text-green-500" />
    case 'audio':
      return <Music size={24} className="text-purple-500" />
    default:
      return <File size={24} className="text-gray-500" />
  }
}

export default function MediaPage() {
  const [filter, setFilter] = useState<string>('')
  const { data: media, isLoading } = useMedia(filter || undefined)
  const uploadMedia = useUploadMedia()
  const deleteMedia = useDeleteMedia()

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      acceptedFiles.forEach((file) => {
        uploadMedia.mutate({ file })
      })
    },
    [uploadMedia]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'video/*': ['.mp4', '.avi', '.mkv', '.mov', '.webm'],
      'image/*': ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'],
      'audio/*': ['.mp3', '.wav', '.ogg', '.flac'],
    },
  })

  const handleDelete = (mediaItem: Media) => {
    if (confirm(`Eliminare "${mediaItem.name}"?`)) {
      deleteMedia.mutate(mediaItem.id)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Media</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="input w-auto"
            >
              <option value="">Tutti i tipi</option>
              <option value="video">Video</option>
              <option value="image">Immagini</option>
              <option value="audio">Audio</option>
            </select>
          </div>
        </div>
      </div>

      {/* Upload area */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors mb-6 ${
          isDragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 hover:border-gray-400 bg-white'
        }`}
      >
        <input {...getInputProps()} />
        <Upload
          size={48}
          className={`mx-auto mb-4 ${
            isDragActive ? 'text-primary-500' : 'text-gray-400'
          }`}
        />
        {isDragActive ? (
          <p className="text-primary-600 font-medium">Rilascia i file qui...</p>
        ) : (
          <>
            <p className="text-gray-600 font-medium">
              Trascina i file qui o clicca per selezionare
            </p>
            <p className="text-sm text-gray-400 mt-1">
              Video (MP4, AVI, MKV), Immagini (JPG, PNG, GIF), Audio (MP3, WAV)
            </p>
          </>
        )}
        {uploadMedia.isPending && (
          <div className="mt-4 flex items-center justify-center gap-2">
            <LoadingSpinner size="sm" />
            <span className="text-sm text-gray-500">Caricamento in corso...</span>
          </div>
        )}
      </div>

      {/* Media grid */}
      {media && media.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {media.map((item) => (
            <div
              key={item.id}
              className="bg-white rounded-xl shadow-sm overflow-hidden card-hover"
            >
              {/* Preview */}
              <div className="h-40 bg-gray-100 flex items-center justify-center overflow-hidden">
                {item.media_type === 'image' ? (
                  <img
                    src={mediaApi.getPreviewUrl(item.filename)}
                    alt={item.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                      e.currentTarget.nextElementSibling?.classList.remove('hidden')
                    }}
                  />
                ) : item.media_type === 'video' ? (
                  <video
                    src={mediaApi.getPreviewUrl(item.filename)}
                    className="w-full h-full object-cover"
                    muted
                    onMouseEnter={(e) => e.currentTarget.play()}
                    onMouseLeave={(e) => {
                      e.currentTarget.pause()
                      e.currentTarget.currentTime = 0
                    }}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none'
                      e.currentTarget.nextElementSibling?.classList.remove('hidden')
                    }}
                  />
                ) : null}
                <div className={item.media_type === 'audio' ? '' : 'hidden'}>
                  <MediaIcon type={item.media_type} />
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <h3 className="font-medium text-gray-900 truncate" title={item.name}>
                  {item.name}
                </h3>
                <p className="text-sm text-gray-500 truncate">{item.filename}</p>
                <div className="flex items-center justify-between mt-2 text-xs text-gray-400">
                  <span className="uppercase">{item.media_type}</span>
                  <span>{item.file_size ? formatBytes(item.file_size) : '-'}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="px-4 py-3 bg-gray-50 flex items-center justify-between border-t">
                <button
                  onClick={() => mediaApi.download(item.id, item.original_filename || item.filename)}
                  className="flex items-center gap-1 text-primary-600 hover:text-primary-700 text-sm"
                >
                  <Download size={14} />
                  Download
                </button>
                <button
                  onClick={() => handleDelete(item)}
                  className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm"
                  disabled={deleteMedia.isPending}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Image size={32} className="text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun file</h3>
          <p className="text-gray-500">
            Carica video, immagini o file audio per utilizzarli nei trigger.
          </p>
        </div>
      )}
    </div>
  )
}
